import { useEffect, useState } from 'react';
import { apiService, STORAGE_BASE_URL } from '../../services/apiService';
import type { Visit } from '@shared/types/visit';

type Mode = 'people' | 'timeline';

interface VisitorRow {
  id: number;
  name: string;
  photo_path: string | null;
  visit_count: number;
  last_seen_at: string;
}

function photoUrl(p: string | null): string | undefined {
  return p ? `${STORAGE_BASE_URL}/storage/photos/${p}` : undefined;
}

export function AdminVisitorsTab({ showToast }: { showToast: (msg: string, type?: 'success' | 'error') => void }) {
  const [mode, setMode] = useState<Mode>('people');
  return (
    <div>
      <h2 className="admin-section-title">Visitantes</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="admin-btn" style={{ opacity: mode === 'people' ? 1 : 0.55 }} onClick={() => setMode('people')}>Pessoas</button>
        <button className="admin-btn" style={{ opacity: mode === 'timeline' ? 1 : 0.55 }} onClick={() => setMode('timeline')}>Linha do tempo</button>
      </div>
      {mode === 'people' ? <PeopleView showToast={showToast} /> : <TimelineView showToast={showToast} />}
    </div>
  );
}

function PeopleView({ showToast }: { showToast: (m: string, t?: 'success' | 'error') => void }) {
  const [rows, setRows] = useState<VisitorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<number | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);

  async function load() {
    setLoading(true);
    try {
      const list = await apiService.getVisitors();
      setRows(list);
      setDraft(Object.fromEntries(list.map((v: VisitorRow) => [v.id, v.name])));
    } catch (e: any) {
      showToast(e.message || 'Erro ao carregar', 'error');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function rename(id: number) {
    const name = (draft[id] ?? '').trim();
    if (!name) return;
    try { await apiService.renameVisitor(id, name); showToast('Nome salvo'); load(); }
    catch (e: any) { showToast(e.message || 'Erro', 'error'); }
  }

  async function toggle(id: number) {
    if (expanded === id) { setExpanded(null); setVisits([]); return; }
    setExpanded(id);
    try { setVisits(await apiService.getVisitorVisits(id)); }
    catch { setVisits([]); }
  }

  async function removeVisitor(id: number) {
    if (!window.confirm('Remover este visitante? As visitas dele continuam na linha do tempo.')) return;
    try {
      await apiService.deleteVisitor(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      showToast('Visitante removido');
    } catch (e: any) {
      showToast(e.message || 'Erro ao remover', 'error');
    }
  }

  const isUnknown = (name: string) => !name || name.toLowerCase().startsWith('desconhecido');

  if (loading) return <p>Carregando...</p>;
  if (rows.length === 0) return <div className="admin-empty">Nenhum visitante ainda.</div>;

  return (
    <div className="admin-video-grid">
      {rows.map((v) => (
        <div key={v.id} className="admin-card">
          {photoUrl(v.photo_path)
            ? <img src={photoUrl(v.photo_path)} alt={v.name} style={{ width: '100%', borderRadius: 8, marginBottom: 8 }} />
            : <div style={{ height: 120, background: 'var(--bg-darker)', borderRadius: 8, marginBottom: 8, display: 'grid', placeItems: 'center' }}>sem foto</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input
              value={draft[v.id] ?? ''}
              onChange={(e) => setDraft((p) => ({ ...p, [v.id]: e.target.value }))}
              placeholder={isUnknown(v.name) ? `Desconhecido #${v.id}` : ''}
              style={{ flex: '1 1 120px', padding: 6, fontSize: 15 }}
            />
            <button className="admin-btn" onClick={() => rename(v.id)}>Salvar</button>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
            {v.visit_count} visita(s) · última {new Date(v.last_seen_at).toLocaleString('pt-BR')}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button className="admin-btn" style={{ flex: 1 }} onClick={() => toggle(v.id)}>
              {expanded === v.id ? 'Ocultar visitas' : 'Ver visitas'}
            </button>
            <button className="admin-btn admin-btn-danger" onClick={() => removeVisitor(v.id)} title="Remover visitante">
              🗑️
            </button>
          </div>
          {expanded === v.id && (
            <div style={{ marginTop: 8 }}>
              {visits.length === 0 && <p style={{ fontSize: 13 }}>Sem visitas registradas.</p>}
              {visits.map((vis) => (
                <div key={vis.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  {photoUrl(vis.photo_path) && <img src={photoUrl(vis.photo_path)} alt="" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />}
                  <span style={{ fontSize: 13 }}>{new Date(vis.created_at).toLocaleString('pt-BR')}{vis.doorbell_id ? ` · campainha ${vis.doorbell_id}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TimelineView({ showToast }: { showToast: (m: string, t?: 'success' | 'error') => void }) {
  const [items, setItems] = useState<Visit[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const PAGE_SIZE = 20;

  async function loadPage(p: number) {
    setLoading(true);
    try {
      const res = await apiService.getVisits(p, PAGE_SIZE);
      setItems((prev) => (p === 1 ? res.items : [...prev, ...res.items]));
      setTotal(res.total);
      setPage(p);
    } catch (e: any) {
      showToast(e.message || 'Erro ao carregar', 'error');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadPage(1); }, []);

  async function name(visitId: number) {
    const n = (draft[visitId] ?? '').trim();
    if (!n) return;
    try {
      await apiService.nameVisit(visitId, n);
      showToast('Visitante batizado');
      loadPage(1);
    } catch (e: any) {
      showToast(e.message || 'Erro', 'error');
    }
  }

  async function removeOne(visitId: number) {
    if (!window.confirm('Remover esta visita?')) return;
    try {
      await apiService.deleteVisit(visitId);
      setItems((prev) => prev.filter((x) => x.id !== visitId));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e: any) {
      showToast(e.message || 'Erro ao remover', 'error');
    }
  }

  async function clearAll() {
    if (!window.confirm('Apagar TODAS as visitas registradas? Não dá para desfazer.')) return;
    try {
      await apiService.clearVisits();
      setItems([]);
      setTotal(0);
      showToast('Tudo apagado');
    } catch (e: any) {
      showToast(e.message || 'Erro ao apagar', 'error');
    }
  }

  const isUnknown = (v: Visit) => !v.visitor_id && (!v.name_snapshot || v.name_snapshot.toLowerCase().startsWith('desconhecido'));

  if (loading && items.length === 0) return <p>Carregando...</p>;
  if (items.length === 0) return <div className="admin-empty">Nenhuma visita registrada.</div>;

  return (
    <div>
      <button className="admin-btn admin-btn-danger" onClick={clearAll} style={{ marginBottom: 12 }}>
        🗑️ Apagar todas
      </button>
      {items.map((v) => (
        <div key={v.id} className="admin-card" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          {v.photo_path
            ? <img src={`${STORAGE_BASE_URL}/storage/photos/${v.photo_path}`} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover' }} />
            : <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--bg-darker)' }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {new Date(v.created_at).toLocaleString('pt-BR')}{v.doorbell_id ? ` · campainha ${v.doorbell_id}` : ''}
            </div>
            {isUnknown(v) ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                <input
                  placeholder="Desconhecido — dar um nome"
                  value={draft[v.id] ?? ''}
                  onChange={(e) => setDraft((p) => ({ ...p, [v.id]: e.target.value }))}
                  style={{ flex: '1 1 140px', padding: 6 }}
                />
                <button className="admin-btn" onClick={() => name(v.id)}>Batizar</button>
              </div>
            ) : (
              <div style={{ fontWeight: 600 }}>{v.name_snapshot || 'Visitante'}</div>
            )}
          </div>
          <button
            className="admin-btn admin-btn-danger"
            onClick={() => removeOne(v.id)}
            title="Remover"
            style={{ flexShrink: 0 }}
          >
            🗑️
          </button>
        </div>
      ))}
      {items.length < total && (
        <button className="admin-btn" onClick={() => loadPage(page + 1)} disabled={loading}>
          {loading ? 'Carregando...' : 'Carregar mais'}
        </button>
      )}
    </div>
  );
}

export default AdminVisitorsTab;
